import { hasSubanalysisInvocation } from "@/domains/templates/subanalysis-capability";
import { useQuery } from "@tanstack/react-query";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { SectionPalette } from "./components/section-palette";
import { TemplateDetailsHeader } from "./components/template-details-header";
import { TemplateAnalysisVariablesSection } from "./components/template-analysis-variables-section";
import { TemplateStep } from "./components/template-step";
import { TemplateEditorSkeleton } from "./components/template-editor-skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { useParamOrThrow } from "@/shared/hooks/use-param-or-throw";
import { CreateReportDialog } from "@/features/reports";
import { CircleAlert, PlusIcon } from "lucide-react";
import { useRecordPermissions, useTenant } from "@/domains/tenant/hooks";
import { templateQueries } from "@/domains/templates/queries";

export function TemplateEditorPage() {
  const uuid = useParamOrThrow("uuid");

  const {
    data: template,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useQuery(templateBuilderQueries.detail(uuid));
  const {
    data: templateSteps,
    isLoading: isLoadingSteps,
    error: templateStepsError,
  } = useQuery(templateBuilderQueries.steps(uuid));
  const { canEdit } = useRecordPermissions();
  const { data: tenant } = useTenant();
  const currentTenantId = tenant?.tenant.tenant_id;
  const { data: catalogVariableGroups = [], isLoading: catalogLoading } =
    useQuery(templateQueries.groupedRootVariables(undefined, currentTenantId));
  const analysisVariables = template?.variables ?? [];
  const variableEditorData = {
    currentTenantId,
    catalogVariableGroups,
    analysisVariables,
    linkedAnalyses: [],
    linkedAnalysisSteps: [],
    isLoading: catalogLoading,
  };

  const isLoading = isLoadingTemplate || isLoadingSteps;
  const error = templateError ?? templateStepsError;

  const stepsCount = templateSteps?.length ?? 0;
  const containsSubanalysis =
    hasSubanalysisInvocation(template?.variables ?? []) ||
    (templateSteps ?? []).some(
      (step) =>
        step.subanalysis.kind !== "ordinary" ||
        hasSubanalysisInvocation(step.variables),
    );
  const isReadOnly = !canEdit(template?.record_tenant_id);

  if (isLoading)
    return (
      <>
        <title>Loading template... - Researchly</title>
        <TemplateEditorSkeleton />
      </>
    );

  if (error)
    return (
      <>
        <title>Error - Researchly</title>
        <Alert variant="destructive">
          <CircleAlert />
          <AlertTitle>Failed to load template</AlertTitle>
          <AlertDescription>
            {String(error instanceof Error ? error.message : error)}
          </AlertDescription>
        </Alert>
      </>
    );

  return (
    <>
      <title>Template Editor - Researchly</title>
      <div className="flex flex-col gap-6">
        <TemplateDetailsHeader
          name={
            template?.template_setting?.display_name ??
            template?.analysis_name ??
            undefined
          }
          context={template?.context ?? null}
          createdAt={template?.record_created ?? undefined}
          updatedAt={template?.record_updated ?? undefined}
          isReadOnly={isReadOnly}
          createReportTrigger={
            <CreateReportDialog
              trigger={
                <Button className="gap-1.5">
                  <PlusIcon className="size-4" />
                  Create report
                </Button>
              }
              sourceTemplate={{
                hasSubanalysisInvocation: containsSubanalysis,
                id: uuid,
                name:
                  template?.template_setting?.display_name ??
                  template?.analysis_name ??
                  null,
                type: template?.analysis_type ?? null,
                context: template?.context ?? null,
                inputFields: template?.inputFields ?? [],
                scopedInputFields: template?.scopedInputFields ?? [],
                steps: (templateSteps ?? []).map((step) => ({
                  id: step.id,
                  name: step.name,
                  order: step.order,
                  inputFields: step.inputFields,
                  scopedInputFields: step.scopedInputFields,
                })),
              }}
            />
          }
        />

        <TemplateAnalysisVariablesSection
          templateId={uuid}
          variables={template?.variables ?? []}
          editorData={variableEditorData}
          isReadOnly={isReadOnly}
        />

        {!isReadOnly && (
          <>
            <Separator />
            <SectionPalette analysisId={uuid} stepsCount={stepsCount} />
          </>
        )}

        <Separator />

        <div className="flex flex-col gap-4">
          {(templateSteps ?? []).map((section) => (
            <TemplateStep
              key={section.id}
              section={section}
              variableEditorData={variableEditorData}
              isReadOnly={isReadOnly || section.isReadOnly}
            />
          ))}
        </div>
      </div>
    </>
  );
}
